import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../ui/tooltip";

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (iso) => {
  if (!iso) return "Ongoing";
  const [y, m, d] = iso.split("-");
  return `${Number(d)} ${MONTH_SHORT[Number(m) - 1]} ${y}`;
};

/** Build 7 §49: per-company effective-dated VAT reporting configuration (VAT-PER-001). */
export default function VatConfigPanel({ company, isAdmin }) {
  const [configs, setConfigs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ effective_from: `${new Date().getFullYear()}-01-01`, reporting_frequency: "QUARTERLY", vat_payment_lag_months: "2" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ effective_from: "", effective_to: "", ongoing: true, reporting_frequency: "QUARTERLY", vat_payment_lag_months: "2" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/companies/${company.id}/vat-configurations`);
      setConfigs(res.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    try {
      await api.post(`/companies/${company.id}/vat-configurations`, {
        effective_from: form.effective_from,
        reporting_frequency: form.reporting_frequency,
        vat_payment_lag_months: Number(form.vat_payment_lag_months),
      });
      toast.success("VAT configuration added");
      setAdding(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setEditForm({
      effective_from: c.effective_from,
      effective_to: c.effective_to || "",
      ongoing: !c.effective_to,
      reporting_frequency: c.reporting_frequency,
      vat_payment_lag_months: String(c.vat_payment_lag_months),
    });
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/vat-configurations/${editingId}`, {
        effective_from: editForm.effective_from,
        effective_to: editForm.effective_to || null, // empty = ongoing
        reporting_frequency: editForm.reporting_frequency,
        vat_payment_lag_months: Number(editForm.vat_payment_lag_months),
      });
      toast.success("VAT configuration updated");
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/vat-configurations/${deleteTarget.id}`);
      toast.success("VAT configuration deleted");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-lg p-5" data-testid="vat-config-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">VAT Configuration — {company.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Reporting frequency per legal entity (VAT-PER). Effective-dated — frequency changes never rewrite history.
          </p>
        </div>
        {isAdmin && !adding && (
          <Button variant="outline" onClick={() => setAdding(true)} data-testid="vat-config-add-button">Add configuration</Button>
        )}
      </div>

      {configs.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="vat-config-empty">No VAT reporting configuration — VAT settlement is not computed for this company.</p>
      ) : (
        <div className="space-y-2" data-testid="vat-config-list">
          {configs.map((c) => (
            <div key={c.id} className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2" data-testid={`vat-config-${c.id}`}>
              <div className="text-sm text-slate-800">
                <span className="font-semibold">{c.reporting_frequency === "QUARTERLY" ? "Quarterly" : "Monthly"} VAT</span>
                <span className="text-slate-500"> · settlement {c.vat_payment_lag_months} month{c.vat_payment_lag_months === 1 ? "" : "s"} after period end</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">{fmtDate(c.effective_from)} → {fmtDate(c.effective_to)}</span>
                {isAdmin && (
                  <div className="flex items-center gap-1" data-testid={`vat-config-actions-${c.id}`}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Edit VAT configuration"
                          className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                          onClick={() => openEdit(c)}
                          data-testid={`vat-config-edit-${c.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Edit VAT configuration</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Delete VAT configuration"
                          className="p-1.5 rounded-md text-slate-400 hover:text-[#B91C1C] hover:bg-red-50"
                          onClick={() => setDeleteTarget(c)}
                          data-testid={`vat-config-delete-${c.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Delete VAT configuration</TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isAdmin && editingId && (
        <div className="mt-3 border border-[#CFE3FC] bg-[#F8FAFC] rounded-lg p-4 space-y-3" data-testid="vat-config-edit-form">
          <p className="text-xs font-semibold text-slate-600">Edit configuration — tick "Ongoing" for no end date</p>
          <div className="grid grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label>Effective from</Label>
              <Input type="date" value={editForm.effective_from} onChange={(e) => setEditForm({ ...editForm, effective_from: e.target.value })} data-testid="vat-config-edit-from" />
            </div>
            <div className="space-y-1.5">
              <Label>Effective to</Label>
              <Input type="date" value={editForm.effective_to} disabled={editForm.ongoing}
                onChange={(e) => setEditForm({ ...editForm, effective_to: e.target.value })} data-testid="vat-config-edit-to" />
              <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer pt-0.5">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-[#0052CC]"
                  checked={editForm.ongoing}
                  onChange={(e) => setEditForm({ ...editForm, ongoing: e.target.checked, effective_to: e.target.checked ? "" : editForm.effective_to })}
                  data-testid="vat-config-edit-ongoing"
                />
                Ongoing (no end date)
              </label>
            </div>
            <div className="space-y-1.5">
              <Label>Reporting frequency</Label>
              <Select value={editForm.reporting_frequency} onValueChange={(v) => setEditForm({ ...editForm, reporting_frequency: v })}>
                <SelectTrigger data-testid="vat-config-edit-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                  <SelectItem value="MONTHLY">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Settlement lag (months)</Label>
              <Input type="number" min="0" max="24" value={editForm.vat_payment_lag_months} onChange={(e) => setEditForm({ ...editForm, vat_payment_lag_months: e.target.value })} data-testid="vat-config-edit-lag" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditingId(null)} data-testid="vat-config-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="vat-config-edit-save">Save changes</Button>
          </div>
        </div>
      )}

      {isAdmin && adding && (
        <div className="mt-3 border border-slate-200 rounded-lg p-4 space-y-3" data-testid="vat-config-form">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Effective from</Label>
              <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="vat-config-from" />
            </div>
            <div className="space-y-1.5">
              <Label>Reporting frequency</Label>
              <Select value={form.reporting_frequency} onValueChange={(v) => setForm({ ...form, reporting_frequency: v })}>
                <SelectTrigger data-testid="vat-config-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="QUARTERLY">Quarterly</SelectItem>
                  <SelectItem value="MONTHLY">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Settlement lag (months)</Label>
              <Input type="number" min="0" max="24" value={form.vat_payment_lag_months} onChange={(e) => setForm({ ...form, vat_payment_lag_months: e.target.value })} data-testid="vat-config-lag" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAdding(false)} data-testid="vat-config-cancel">Cancel</Button>
            <Button onClick={add} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="vat-config-save">Save</Button>
          </div>
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="vat-config-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete VAT configuration?</DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="text-sm text-slate-600 space-y-1" data-testid="vat-config-delete-summary">
              <p className="font-semibold text-slate-900" data-testid="vat-config-delete-company">{company.name}</p>
              <p className="font-semibold text-slate-900">
                {deleteTarget.reporting_frequency === "QUARTERLY" ? "Quarterly" : "Monthly"}
              </p>
              <p>{fmtDate(deleteTarget.effective_from)} → {fmtDate(deleteTarget.effective_to)}</p>
              <p className="pt-1">Only this record is removed — periods it covered become explicitly uncovered in VAT reporting.</p>
              <p className="font-medium text-[#B91C1C]">This action cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="vat-config-delete-cancel">Cancel</Button>
            <Button onClick={confirmDelete} disabled={busy} className="bg-[#B91C1C] hover:bg-[#991B1B]" data-testid="vat-config-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
